import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  Select,
  Table,
  Tag,
  Statistic,
  Typography,
  Spin,
  Space,
  theme,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import api from "@/libs/api";
import PageLayout from "@components/pageLayout/PageLayout.react";
import { makePagination } from "@components/common/TablePagination";

const { Title, Text } = Typography;

export default function GroupManagement() {
  const { token } = theme.useToken();
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [systems, setSystems] = useState([]);
  const [statusData, setStatusData] = useState({ online: 0, offline: 0, total: 0 });
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchGroups();
  }, []);

  useEffect(() => {
    if (selectedGroup) {
      fetchSystemsAndStatus(selectedGroup);
    }
  }, [selectedGroup]);

  const fetchGroups = async () => {
    try {
      const userId = localStorage.getItem("userId");
      const res = await api.get(`/api/groups?userId=${userId}`);
      if (res.data.success && res.data.data) {
        setGroups(res.data.data);
        if (res.data.data.length > 0) {
          setSelectedGroup(res.data.data[0].id);
        }
      }
    } catch (err) {
      console.error("Failed to fetch groups:", err);
      setGroups([]);
    }
  };

  const fetchSystemsAndStatus = async (groupId) => {
    setLoading(true);
    try {
      const [systemsRes, statusRes] = await Promise.all([
        api.get(`/api/groups/${groupId}/systems`),
        api.get(`/api/groups/${groupId}/status`),
      ]);

      if (systemsRes.data.success) {
        setSystems(systemsRes.data.data || []);
      }
      if (statusRes.data.success) {
        setStatusData(statusRes.data.data || { online: 0, offline: 0, total: 0 });
      }
    } catch (err) {
      console.error("Failed to fetch systems:", err);
      setSystems([]);
      setStatusData({ online: 0, offline: 0, total: 0 });
    } finally {
      setLoading(false);
    }
  };

  // Table columns
  const columns = [
    {
      title: "Serial Number",
      dataIndex: "sn",
      key: "sn",
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "NMI",
      dataIndex: "nmi",
      key: "nmi",
    },
    {
      title: "Inverter (kW)",
      dataIndex: "invKw",
      key: "invKw",
      render: (val) => val?.toFixed(1) || "-",
      align: "right",
    },
    {
      title: "Battery (kWh)",
      dataIndex: "batteryKwh",
      key: "batteryKwh",
      render: (val) => val?.toFixed(1) || "-",
      align: "right",
    },
    {
      title: "Status",
      dataIndex: "online",
      key: "online",
      render: (online) => (
        <Tag
          icon={online ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
          color={online ? "success" : "error"}
        >
          {online ? "Online" : "Offline"}
        </Tag>
      ),
    },
    {
      title: "Offboarded",
      dataIndex: "offboarded",
      key: "offboarded",
      render: (offboarded) => (
        <Tag color={offboarded ? "default" : "blue"}>
          {offboarded ? "Yes" : "No"}
        </Tag>
      ),
    },
  ];

  const groupOptions = groups.map((g) => ({
    value: g.id,
    label: g.name,
  }));

  return (
    <PageLayout active="groupManagement">
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ marginBottom: 4 }}>
          Group Management
        </Title>
        <Text type="secondary" style={{ fontSize: 16 }}>
          Manage and monitor your VPP systems by group
        </Text>
      </div>

      {/* Group Selector */}
      <Card
        bordered={false}
        style={{ marginBottom: 24, borderRadius: 12 }}
      >
        <Space direction="vertical" size={8}>
          <Text type="secondary">Select Group</Text>
          <Select
            value={selectedGroup}
            onChange={setSelectedGroup}
            options={groupOptions}
            style={{ width: 300 }}
            size="large"
            placeholder="Select a group"
          />
        </Space>
      </Card>

      {/* Status Cards */}
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Online Systems"
              value={statusData.online}
              valueStyle={{ color: token.colorSuccess }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Offline Systems"
              value={statusData.offline}
              valueStyle={{ color: token.colorError }}
              prefix={<CloseCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Total Systems"
              value={statusData.total}
              valueStyle={{ color: token.colorPrimary }}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Systems Table */}
      <Card
        title="System List"
        bordered={false}
        style={{ borderRadius: 12 }}
        styles={{
          header: { borderBottom: `1px solid ${token.colorBorderSecondary}` },
        }}
      >
        <Table
          columns={columns}
          dataSource={systems}
          rowKey="id"
          loading={loading}
          pagination={makePagination(pageSize, setPageSize)}
        />
      </Card>
    </PageLayout>
  );
}
